import { Box, Stack } from '@mui/system';
import { Card, CardHeader, Typography, CardContent } from '@mui/material';

import { useTranslate } from 'src/locales';
import { HEADER } from 'src/layouts/config-layout';

import Iconify from 'src/components/iconify';
import DocumentViewer from 'src/components/documents/document-viewer';

import ApplicationNotes from './application-notes';

// ----------------------------------------------------------------------

type Props = {
  applicationId: string;
  internalThreadId?: string;
  resumeUrl?: string;
};

// Fills the screen under the dashboard header once the application header has scrolled past, so the
// resume and the notes each scroll on their own and the composer stays beside the page being read.
const PANE_HEIGHT = `calc(100vh - ${HEADER.H_DESKTOP + 32}px)`;

/**
 * The application's Resume tab (JT1-1825): the resume on the left and the application's notes on the
 * right, so a recruiter can write notes while reading. The notes are the same card as the Profile
 * tab's sidebar, written to the application's internal thread.
 */
export default function ApplicationResumeTab({ applicationId, internalThreadId, resumeUrl }: Props) {
  const { t } = useTranslate();

  return (
    <Box
      sx={{
        px: 3,
        gap: 3,
        display: 'grid',
        alignItems: 'start',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 7fr) minmax(0, 3fr)' },
      }}
    >
      {resumeUrl ? (
        <Box sx={{ height: { xs: '70vh', md: PANE_HEIGHT } }}>
          <DocumentViewer fileUrl={resumeUrl} />
        </Box>
      ) : (
        <Typography sx={{ py: 5 }} align="center" variant="body2">
          {t('profile.documents.no_resume')}
        </Typography>
      )}

      <Card sx={{ maxHeight: { md: PANE_HEIGHT }, overflowY: 'auto' }}>
        <CardHeader
          title={
            <Stack direction="row" gap={1} justifyContent="start" alignItems="center">
              <Iconify icon="uil:notes" />
              <Box>{t('applications.detail.candidate_profile.notes')}</Box>
            </Stack>
          }
        />
        <CardContent>
          <ApplicationNotes applicationId={applicationId} internalThreadId={internalThreadId} />
        </CardContent>
      </Card>
    </Box>
  );
}
